import { type ReactNode, useEffect, useMemo } from 'react';
import { Auth0Context, type Auth0ContextInterface } from '@auth0/auth0-react';
import { useUserPreferencesStore } from '@/shared/stores';
import { isE2ETestMode } from './utils/e2e';

const E2E_ORG_ID = 'e2e-test-org-id';
const E2E_WORKSPACE_ID = 'e2e-test-workspace-id';
const E2E_PROVIDER_ID = 'e2e-test-provider-id-12345';

interface TestAuthProviderProps {
  children: ReactNode;
}

export const TestAuthProvider = ({ children }: TestAuthProviderProps) => {
  // eslint-disable-next-line no-restricted-syntax -- reason: Check E2E test mode on mount
  useEffect(() => {
    // Check for E2E test mode (SSR-safe: checks build-time VITE_E2E_TEST_MODE first,
    // then runtime window.__ENV__ if available)
    const isE2EMode = isE2ETestMode();

    if (!isE2EMode) return;
    const currentState = useUserPreferencesStore.getState();
    if (
      currentState.currentOrganizationId !== E2E_ORG_ID ||
      currentState.currentWorkspaceId !== E2E_WORKSPACE_ID
    ) {
      useUserPreferencesStore.setState({
        currentOrganizationId: E2E_ORG_ID,
        currentWorkspaceId: E2E_WORKSPACE_ID,
      });
    }
  }, []);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const mockAuthContext: Auth0ContextInterface = useMemo(
    () =>
      ({
        error: undefined,
        isAuthenticated: true,
        isLoading: false,
        user: {
          sub: E2E_PROVIDER_ID,
          name: 'E2E Test User',
          email: 'e2e-test@kognitos.com',
          picture: 'https://via.placeholder.com/150',
        } as any,
        getAccessTokenSilently: (async () =>
          'test-token-eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9') as any,
        loginWithRedirect: async () => {},
        logout: async () => {},
      }) as Auth0ContextInterface,
    [],
  );
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return <Auth0Context value={mockAuthContext}>{children}</Auth0Context>;
};
